/**
 * Customers Page - with Server-Side Pagination, Sorting and Selection
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataGrid } from '../../components/ui/DataGrid';
import PageHeader from '../../components/ui/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Plus, Users, Edit, Trash2, Phone, Mail, Download, FileText, FileSpreadsheet, ChevronDown, CheckSquare, ShoppingBag } from 'lucide-react';

import CustomerModal from './CustomerModal';
import CustomerSalesModal from './CustomerSalesModal';
import './styles.css';

export default function Customers() {
  const { t } = useTranslation();
  const { formatDate } = useInstance();
  const gridRef = useRef(null);

  // Data state
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  // Sales history modal state
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [selectedCustomerForSales, setSelectedCustomerForSales] = useState(null);

  // Server-side pagination state
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({});

  // Selection state
  const [selectedCount, setSelectedCount] = useState(0);

  // Load customers with server-side params
  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build filters object
      const apiFilters = {};
      if (filters.name) apiFilters.name = filters.name;
      if (filters.status && filters.status !== 'all') apiFilters.is_active = filters.status === 'active';

      const response = await api.post('/customers/list', {
        pageNumber,
        pageSize,
        sortBy,
        sortDirection,
        filters: Object.keys(apiFilters).length > 0 ? apiFilters : undefined,
      });

      if (response.data?.success) {
        setCustomers(response.data.data?.items || []);
        setTotalItems(response.data.data?.totalItems || 0);
      } else {
        setCustomers(response.data?.data?.items || response.data || []);
        setTotalItems(response.data?.data?.totalItems || response.data?.length || 0);
      }
    } catch (err) {
      console.error('Error loading customers:', err);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, sortBy, sortDirection, filters, t]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Handle page change from grid
  const handlePageChange = useCallback((newPage, newPageSize) => {
    setPageNumber(newPage);
    setPageSize(newPageSize);
  }, []);

  // Handle sort change from grid
  const handleSortChange = useCallback((newSortBy, newSortDirection) => {
    if (newSortBy && newSortDirection) {
      setSortBy(newSortBy);
      setSortDirection(newSortDirection);
    }
  }, []);

  // Handle filter search
  const handleSearch = useCallback(() => {
    setPageNumber(1);
    loadCustomers();
  }, [loadCustomers]);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setPageNumber(1);
  }, []);

  const handleSave = async (data) => {
    try {
      setSaving(true);
      if (editingCustomer?.id) {
        await api.put(`/customers/${editingCustomer.id}`, data);
      } else {
        await api.post('/customers', data);
      }
      toast.success(t('customers.saveSuccess'));
      setModalOpen(false);
      setEditingCustomer(null);
      await loadCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close - reset editingCustomer to null
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => setEditingCustomer(null), 100);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/customers/${deleteId}`);
      toast.success(t('customers.deleteSuccess'));
      setDeleteId(null);
      await loadCustomers();
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  // Export handlers
  const handleExportCSV = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    gridRef.current?.exportCSV(onlySelected);
  }, [selectedCount]);

  const handleExportJSON = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    const data = gridRef.current?.exportJSON(onlySelected);
    if (!data?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clientes_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedCount]);

  // Track selection changes
  const handleSelectionChanged = useCallback(() => {
    const count = gridRef.current?.getSelectedCount() || 0;
    setSelectedCount(count);
  }, []);

  const filterDefinitions = useMemo(() => [
    {
      key: 'name',
      label: 'Nome/Email/Telefone',
      type: 'text',
      placeholder: 'Buscar cliente...',
    },
    {
      key: 'status',
      label: t('common.status'),
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'active', label: 'Ativo' },
        { value: 'inactive', label: 'Inativo' },
      ],
    },
  ], [t]);

  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: t('customers.name'),
      flex: 2,
      sortable: true,
      cellRenderer: (params) => (
        <div className="flex items-center gap-3">
          <div className="customer-avatar">
            <Users className="h-4 w-4" />
          </div>
          <div>
            <p className="font-medium">{params.value}</p>
            {params.data.address && (
              <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                {params.data.address}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      field: 'contact',
      headerName: t('customers.phone'),
      flex: 1.5,
      sortable: false,
      valueGetter: (params) => params.data.phone || params.data.email,
      cellRenderer: (params) => (
        <div className="flex flex-col gap-0.5">
          {params.data.phone && (
            <div className="flex items-center gap-1 text-sm">
              <Phone className="h-3 w-3" />
              {params.data.phone}
            </div>
          )}
          {params.data.email && (
            <div className="flex items-center gap-1 text-sm">
              <Mail className="h-3 w-3" />
              {params.data.email}
            </div>
          )}
        </div>
      ),
    },
    {
      field: 'cpf',
      headerName: t('customers.cpf'),
      flex: 1,
      sortable: true,
      valueFormatter: (params) => params.value || '-',
    },
    {
      field: 'created_at',
      headerName: 'Cadastro',
      flex: 1,
      sortable: true,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'is_active',
      headerName: t('common.status'),
      flex: 0.8,
      sortable: true,
      cellRenderer: (params) => (
        <span className={params.value ? 'datagrid-status-active' : 'datagrid-status-inactive'}>
          {params.value ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      flex: 1,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div className="datagrid-actions">
          <Button
            variant="ghost"
            size="icon"
            title="Histórico de Compras"
            onClick={() => {
              setSelectedCustomerForSales(params.data);
              setSalesModalOpen(true);
            }}
          >
            <ShoppingBag className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            title="Editar"
            onClick={() => {
              setEditingCustomer(params.data);
              setModalOpen(true);
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            title="Excluir"
            onClick={() => setDeleteId(params.data.id)}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ], [t, formatDate]);

  // Export actions dropdown
  const ExportActions = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exportar
          {selectedCount > 0 && (
            <Badge variant="secondary" className="ml-2">{selectedCount}</Badge>
          )}
          <ChevronDown className="h-3 w-3 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExportCSV(false)}>
          <FileText className="h-4 w-4 mr-2" />
          Exportar Tudo (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExportJSON(false)}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Exportar Tudo (JSON)
        </DropdownMenuItem>
        {selectedCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleExportCSV(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (CSV)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportJSON(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (JSON)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="customers-page" data-testid="customers-page">
      <PageHeader
        icon={Users}
        title={t('customers.title')}
        subtitle={`${totalItems} clientes cadastrados`}
        actions={
          <div className="flex items-center gap-2">
            <ExportActions />
            <Button
              onClick={() => { setEditingCustomer(null); setModalOpen(true); }}
              data-testid="new-customer-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('customers.newCustomer')}
            </Button>
          </div>
        }
        filters={filterDefinitions}
        filterValues={filters}
        onFilterChange={setFilters}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        showExport={false}
      />

      <DataGrid
        ref={gridRef}
        title="Lista de Clientes"
        columnDefs={columnDefs}
        rowData={customers}
        loading={loading}
        enableSelection={true}
        height="600px"
        getRowId={(params) => params.data.id}
        serverSide={true}
        totalItems={totalItems}
        pageNumber={pageNumber}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        sortBy={sortBy}
        sortDirection={sortDirection}
        paginationPageSize={pageSize}
        onSelectionChanged={handleSelectionChanged}
      />

      <CustomerModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        customer={editingCustomer}
        onSave={handleSave}
        loading={saving}
      />

      <CustomerSalesModal
        open={salesModalOpen}
        onOpenChange={setSalesModalOpen}
        customer={selectedCustomerForSales}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('customers.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
